/* eslint-disable react/no-children-prop */
import React, { useCallback, useState, useRef, useEffect } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import {
  Box,
  Button,
  InputGroup,
  Input,
  Popover,
  Text,
  Stack,
  Link,
  InputLeftElement,
  InputRightElement,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverCloseButton,
  Portal,
  VStack,
  FormControl,
  FormErrorMessage,
  useDisclosure,
  Flex,
} from "@chakra-ui/react";
import { EmailIcon } from "@chakra-ui/icons";
import { HiOutlinePencil } from "react-icons/hi";
import * as env from "env";

import useGetPrimaryEmail from "api/accounts/useGetPrimaryEmail";
import EmailVerification from "components/EmailVerification";
import ExternalEmailModLink from "components/ExternalEmailModLink";
import { emailRegex } from "utils/emailValidation";
import { useSettings } from "utils/settingsContext";

const SPACING = 4;

const NoEmailExistMessage = () => (
  <Stack spacing={4} px={2}>
    <Text fontSize="lg">
      <FormattedMessage
        defaultMessage="To start, we need to collect your email"
        description="Header title"
      />
    </Text>
    <Text fontSize="sm" color="secondaryText">
      <FormattedMessage
        defaultMessage="We will send updates, receipts, tax info, and all communication to the email address we have
        in our system in accordance with our <link>Privacy Policy</link>."
        description="Privacy policy text and link"
        values={{
          link: (msg) => (
            <Link href={env.TILIA_PRIVACY_URL} isExternal color="integrator.400" fontSize="sm">
              {msg}
            </Link>
          ),
        }}
      />
    </Text>
  </Stack>
);

const EmailNotVerifiedMessage = () => (
  <Stack spacing={4} px={2}>
    <Text fontSize="lg">
      <FormattedMessage
        defaultMessage="To start, we need to verify your email"
        description="Header title"
      />
    </Text>
    <Text fontSize="sm" color="secondaryText">
      <FormattedMessage
        defaultMessage="We will send updates, receipts, tax info, and all communication to the email address we have
        in our system in accordance with our <link>Privacy Policy</link>."
        description="Privacy policy text and link"
        values={{
          link: (msg) => (
            <Link href={env.TILIA_PRIVACY_URL} isExternal color="integrator.400" fontSize="sm">
              {msg}
            </Link>
          ),
        }}
      />
    </Text>
  </Stack>
);

const EmailVerifiedMessage = (disablePrimaryEmailUpdateByUser) => () => (
  <Stack spacing={4} px={2}>
    <Text fontSize="lg">
      <FormattedMessage defaultMessage="Your email is verified." description="Header title" />
    </Text>
    <Text fontSize="sm" color="secondaryText">
      <FormattedMessage
        defaultMessage="We will send updates, receipts, tax info, and all communication to the email address we have
        in our system in accordance with our <link>Privacy Policy</link>."
        description="Privacy policy text and link"
        values={{
          link: (msg) => (
            <Link href={env.TILIA_PRIVACY_URL} isExternal color="integrator.400" fontSize="sm">
              {msg}
            </Link>
          ),
        }}
      />
    </Text>
    {!disablePrimaryEmailUpdateByUser && (
      <Text fontSize="sm" color="secondaryText">
        <FormattedMessage
          defaultMessage="If you wish to receive updates to a different email address, please update your email address below."
          description="Pop up text"
        />
      </Text>
    )}
  </Stack>
);

/**
 * @callback onSendCallback
 * @param {string} email
 */

/**
 * EmailMeButton
 * Presentational component for displaying meta info like dates, status, etc.
 * If the wrapping component wants to optionaly control the popover, it can
 * pass in the useDiclosure props isOpen, onClose, and onToggle.
 * If not supplied, the component will take care of controlling the popover itself.
 * @param {object} props
 * @param {string} props.buttonText
 * @param {Icon} [props.leftButtonIcon]
 * @param {string} [props.isFullWidth = false]
 * @param {string} [props.isLoading = false]
 * @param {onSendCallback} props.onSend
 * @param {boolean} [props.isOpen]
 * @param {function} [props.onToggle]
 * @param {function} [props.onClose]
 * @returns JSX.Element
 */
export default function EmailMeButton({
  buttonText,
  leftButtonIcon,
  isFullWidth = false,
  isLoading = false,
  onSend,
  isOpen,
  onToggle,
  onClose,
}) {
  const { formatMessage } = useIntl();
  const { integratorSettings } = useSettings();
  const [disablePrimaryEmailUpdateByUser] = useState(integratorSettings?.feature_flags?.disablePrimaryEmailUpdateByUser || false);
  const inputRef = useRef();
  const [email, setEmail] = useState(null);
  const [emailInput, setEmailInput] = useState("");
  const [emailMessage, setEmailMessage] = useState(null);
  const [inputIsActive, setInputIsActive] = useState(false);
  const [hasBlurred, setHasBlurred] = useState(false);
  const triggerButtonProps = onToggle ? { onClick: onToggle } : {}; // if onToggle is supplied, it means useDisclosure values are being sent through so we'll use them
  const popoverProps = onClose && (isOpen === false || isOpen) ? { isOpen, onClose } : {}; // if onClose and isOpen are supplied, it means useDisclosure values are being sent through so we'll use them

  const { data, isSuccess, refetch, isError } = useGetPrimaryEmail();

  const {
    isOpen: isEmailVerificationOpen,
    onOpen: onEmailVerificationOpen,
    onClose: onEmailVerificationClose,
  } = useDisclosure();

  useEffect(() => {
    if (isSuccess) {
      const originalEmail = data.payload.email;
      const isVerified = data.payload.is_verified;

      if (originalEmail && isVerified) {
        setEmail(originalEmail);
        setEmailInput(originalEmail);
        setEmailMessage((EmailVerifiedMessage(disablePrimaryEmailUpdateByUser)));
      } else if (originalEmail && !isVerified) {
        setEmailInput(originalEmail);
        setInputIsActive(!disablePrimaryEmailUpdateByUser); // in this case on disable edit state if disablePrimaryEmailUpdateByUser == true
        setEmailMessage(EmailNotVerifiedMessage);
      } else {
        setInputIsActive(true);
        setEmailMessage(NoEmailExistMessage);
      }
    } else if (isError) {
      setInputIsActive(true);
      setEmailMessage(NoEmailExistMessage);
    }
  }, [data, email, isSuccess, isError, setInputIsActive, disablePrimaryEmailUpdateByUser]);

  const handleInputChange = (e) => {
    setEmailInput(e.target.value);
  };
  const handleInputBlur = () => {
    if (!hasBlurred) setHasBlurred(true);
  };

  const handleSend = () => {
    if (onSend) {
      onSend(email);
    }
  };

  const handleEmailVerificationClick = () => {
    onEmailVerificationOpen();
  };

  const handleEmailVerificationSuccess = useCallback(() => {
    refetch();
    setInputIsActive(false);
  }, [refetch, setInputIsActive]);

  const toggleInputActive = () => {
    setInputIsActive(!inputIsActive);
  };

  const handleEmailInputCancel = () => {
    setEmailInput(email);
    toggleInputActive();
  };

  const isValid = () => {
    return emailRegex.test(emailInput);
  };
  const isInvalid = !isValid();
  const showErr = isInvalid && hasBlurred;

  const requiresVerification = !email;

  return (
    <Popover placement="top" {...popoverProps}>
      <PopoverTrigger>
        <Button
          leftIcon={leftButtonIcon || null}
          width={isFullWidth ? "100%" : "auto"}
          {...triggerButtonProps}
          isDisabled={popoverProps.isOpen}
        >
          {buttonText}
        </Button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent data-testid="email-me-button-popover">
          <PopoverHeader border="0" />
          <PopoverCloseButton size="md" />
          <PopoverBody>
            <FormControl isInvalid={showErr}>
              <VStack spacing={SPACING} justify="center">
                {emailMessage}
                <InputGroup>
                  <InputLeftElement
                    pointerEvents="none"
                    children={<EmailIcon color="tertiaryText" />}
                  />
                  <Input
                    aria-label="E-mail"
                    ref={inputRef}
                    type="email"
                    placeholder={formatMessage({
                      defaultMessage: "E-mail",
                      description: "Placeholder text",
                    })}
                    value={emailInput}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    isDisabled={!inputIsActive}
                  />
                  {!disablePrimaryEmailUpdateByUser && !inputIsActive && (
                    <InputRightElement
                      cursor="pointer"
                      children={<HiOutlinePencil color="tertiaryText" />}
                      data-testid="edit-email-address-button"
                      onClick={toggleInputActive}
                    />
                  )}
                </InputGroup>
                {showErr ? (
                  <FormErrorMessage width="100%">
                    <FormattedMessage
                      defaultMessage="Invalid e-mail."
                      description="Error message"
                    />
                  </FormErrorMessage>
                ) : null}
                <ExternalEmailModLink primaryEmailPayload={data?.payload} />
                {inputIsActive || (disablePrimaryEmailUpdateByUser && data?.payload?.is_verified === false) ? (
                  <Flex width="100%" flexWrap="wrap">
                    {!requiresVerification ? (
                      <Button
                        marginBottom="6px"
                        width="100%"
                        size="sm"
                        colorScheme="gray"
                        onClick={handleEmailInputCancel}
                      >
                        <FormattedMessage defaultMessage="Cancel" description="Button text" />
                      </Button>
                    ) : (
                      <Box />
                    )}
                    <Button
                      width="100%"
                      size="sm"
                      isDisabled={isInvalid}
                      onClick={handleEmailVerificationClick}
                      isLoading={isLoading}
                    >
                      {disablePrimaryEmailUpdateByUser ? (
                        <FormattedMessage defaultMessage="Verify email" description="Button label" />
                      ) : (
                        <FormattedMessage defaultMessage="Update email" description="Button text" />
                      )}
                    </Button>
                  </Flex>
                ) : (
                  <Flex justifyContent="flex-end" width="100%">
                    <Button
                      size="sm"
                      isDisabled={isInvalid}
                      onClick={handleSend}
                      isLoading={isLoading}
                    >
                      <FormattedMessage
                        defaultMessage="Send to this email"
                        description="Button text"
                      />
                    </Button>
                  </Flex>
                )}
              </VStack>
            </FormControl>
          </PopoverBody>
        </PopoverContent>
      </Portal>
      <EmailVerification
        email={emailInput}
        isOpen={isEmailVerificationOpen}
        onClose={onEmailVerificationClose}
        onSuccess={handleEmailVerificationSuccess}
      />
    </Popover>
  );
}
